import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Lang } from "@/i18n/translations";
import { useSectionNav } from "@/lib/useSectionNav";
import { PHONE_DISPLAY, PHONE_HREF } from "@/lib/site";

const NAV_ITEMS: { key: string; hash?: string; path?: string }[] = [
  { key: "services", hash: "#services" },
  { key: "work", hash: "#work" },
  { key: "builders", hash: "#builders" },
  { key: "r3", hash: "#r3" },
  { key: "about", hash: "#about" },
  { key: "reviews", path: "/reviews" },
  { key: "contact", hash: "#contact" },
];

export const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const goTo = useSectionNav();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const handleNav = (item: { key: string; hash?: string; path?: string }) => {
    if (item.path) navigate(item.path);
    else if (item.hash) goTo(item.hash);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const navLabel = (key: string) => (t.nav as Record<string, string>)[key];

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-colors duration-500 ${
          scrolled ? "border-white/10 bg-ink/80" : "border-white/5 bg-ink/50"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-[1600px] items-center justify-between gap-4 px-5 md:px-10">
          <button
            data-testid="header-logo"
            onClick={() => goTo("#top")}
            className="group flex items-baseline gap-2 text-left"
          >
            <span className="font-display text-xl font-black tracking-tight text-white md:text-2xl">
              {t.brand.short}
            </span>
            <span className="hidden text-[10px] font-bold tracking-[0.3em] text-neutral-400 sm:inline">
              {t.brand.sub}
            </span>
          </button>

          <nav className="hidden items-center gap-7 lg:flex" data-testid="header-nav">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.key}
                data-testid={`nav-link-${item.key}`}
                onClick={() => handleNav(item)}
                className="text-[11px] font-bold uppercase tracking-[0.18em] text-neutral-400 transition-colors duration-300 hover:text-gold"
              >
                {navLabel(item.key)}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <div
              className="flex items-center rounded-full border border-white/15 p-1"
              data-testid="lang-toggle"
            >
              {(["en", "es"] as Lang[]).map((l) => (
                <button
                  key={l}
                  data-testid={`lang-toggle-${l}`}
                  onClick={() => setLang(l)}
                  className={`rounded-full px-3 py-1 text-[11px] font-bold tracking-widest transition-colors duration-300 ${
                    lang === l ? "bg-gold text-ink" : "text-neutral-400 hover:text-white"
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>

            <a
              data-testid="header-call-btn"
              href={PHONE_HREF}
              className="hidden items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-xs font-bold tracking-wide text-white transition-colors duration-300 hover:border-gold/60 hover:text-gold md:flex"
            >
              <Phone className="h-3.5 w-3.5" />
              {PHONE_DISPLAY}
            </a>

            <Link
              data-testid="header-request-bid-btn"
              to="/request-a-bid"
              className="group flex items-center gap-1.5 rounded-full bg-gold px-4 py-2.5 text-[11px] font-black uppercase tracking-[0.12em] text-ink transition-colors duration-300 hover:bg-golddark md:px-6 md:py-3 md:text-xs"
            >
              {t.nav.requestBid}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>

            <button
              data-testid="mobile-menu-btn"
              onClick={() => setOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white lg:hidden"
              aria-label={t.nav.menu}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-ink"
          >
            <div className="flex h-[72px] items-center justify-between px-5">
              <span className="font-display text-xl font-black tracking-tight text-white">
                {t.brand.short}
              </span>
              <button
                data-testid="mobile-menu-close-btn"
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white"
                aria-label={t.nav.close}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-6">
              {NAV_ITEMS.map((item, i) => (
                <motion.button
                  key={item.key}
                  data-testid={`mobile-nav-link-${item.key}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => handleNav(item)}
                  className="group flex items-baseline gap-4 border-b border-white/10 py-4 text-left"
                >
                  <span className="text-xs font-bold text-gold">0{i + 1}</span>
                  <span className="font-display text-4xl font-black uppercase tracking-tight text-white transition-colors duration-300 group-hover:text-gold">
                    {navLabel(item.key)}
                  </span>
                </motion.button>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="mt-8 flex flex-col gap-3"
              >
                <Link
                  data-testid="mobile-menu-bid-btn"
                  to="/request-a-bid"
                  className="flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 text-sm font-black uppercase tracking-[0.12em] text-ink"
                >
                  {t.nav.requestBid}
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
                <a
                  data-testid="mobile-menu-call-btn"
                  href={PHONE_HREF}
                  className="flex items-center justify-center gap-2 rounded-full border border-white/20 px-6 py-4 text-sm font-bold text-white"
                >
                  <Phone className="h-4 w-4" />
                  {PHONE_DISPLAY}
                </a>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
